import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { vaccinationOutcomeReasonApi } from "api/immunization";
import { VACCINATION_STATUSES, humanise } from "config/immunizationConstants";
import MasterModalShell from "./common/MasterModalShell";
import CodeField from "./common/CodeField";
import { validateCode, validateRequired } from "./common/validation";

const VaccinationOutcomeReasonModal = ({ open, onClose, data }) => {
  const recordId = data?._id;
  const isUpdate = Boolean(recordId);

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      code: data?.code || "",
      display: data?.display || "",
      description: data?.description || "",
      appliesToStatus: data?.appliesToStatus || "NOT_DONE",
      countsAsDecline: Boolean(data?.countsAsDecline),
      requiresNote: Boolean(data?.requiresNote),
      requiresFollowUpDate: Boolean(data?.requiresFollowUpDate),
      sequence: data?.sequence ?? 0,
    });
  }, [open, data]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const notDone = form.appliesToStatus === "NOT_DONE";

  const validationError = useMemo(
    () =>
      validateCode(form.code, { isUpdate, label: "Reason code" }) ||
      validateRequired(form.display, "Display name") ||
      (!notDone && form.countsAsDecline ?
        "Only a reason applying to NOT_DONE may be counted as a patient decline"
      : ""),
    [form, isUpdate, notDone],
  );

  const handleSubmit = async () => {
    if (validationError) return;
    setSaving(true);

    const payload = {
      display: form.display.trim(),
      description: form.description?.trim() || "",
      appliesToStatus: form.appliesToStatus,
      countsAsDecline: form.countsAsDecline,
      requiresNote: form.requiresNote,
      requiresFollowUpDate: form.requiresFollowUpDate,
      sequence: Number(form.sequence) || 0,
    };

    try {
      if (isUpdate) {
        await vaccinationOutcomeReasonApi.update(recordId, payload);
        toast.success("Vaccination outcome reason updated successfully");
      } else {
        await vaccinationOutcomeReasonApi.create({
          ...payload,
          code: form.code.trim(),
        });
        toast.success("Vaccination outcome reason created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} outcome reason`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterModalShell
      open={open}
      onClose={onClose}
      isUpdate={isUpdate}
      entityLabel="Outcome Reason"
      saving={saving}
      validationError={validationError}
      onSubmit={handleSubmit}
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        <CodeField
          value={form.code}
          onChange={(value) => set({ code: value })}
          isUpdate={isUpdate}
          label="Reason Code"
          helperText='e.g. "refused" becomes REFUSED'
        />
        <TextField
          label="Display Name"
          required
          fullWidth
          size="small"
          value={form.display || ""}
          onChange={(event) => set({ display: event.target.value })}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          select
          label="Applies To Status"
          size="small"
          sx={{ minWidth: 220 }}
          value={form.appliesToStatus || "NOT_DONE"}
          onChange={(event) => {
            const appliesToStatus = event.target.value;
            set({
              appliesToStatus,
              ...(appliesToStatus === "NOT_DONE" ?
                {}
              : { countsAsDecline: false }),
            });
          }}
        >
          {VACCINATION_STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {humanise(status)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Sequence"
          type="number"
          size="small"
          sx={{ minWidth: 120 }}
          value={form.sequence}
          onChange={(event) => set({ sequence: event.target.value })}
        />
      </Box>

      <TextField
        label="Description"
        fullWidth
        size="small"
        multiline
        minRows={2}
        value={form.description || ""}
        onChange={(event) => set({ description: event.target.value })}
      />

      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.countsAsDecline)}
              disabled={!notDone}
              onChange={(event) =>
                set({ countsAsDecline: event.target.checked })
              }
            />
          }
          label="Counts as a patient decline"
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", ml: 4, mt: -0.5, mb: 1 }}
        >
          Separates "the system failed to offer it" from "the patient declined"
          in compliance reporting.
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.requiresNote)}
              onChange={(event) => set({ requiresNote: event.target.checked })}
            />
          }
          label="Requires an explanatory note"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.requiresFollowUpDate)}
              onChange={(event) =>
                set({ requiresFollowUpDate: event.target.checked })
              }
            />
          }
          label="Requires a follow-up date"
        />
      </Box>
    </MasterModalShell>
  );
};

export default VaccinationOutcomeReasonModal;
